import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import * as QueryString from "query-string";

// Context
import {InstanceContext} from "../../../context/InstanceContext";

// Components
import CopyUrl from "../../../component/buttons/CopyUrl";
import PageContent from "../../page_content"
import ComparisonOptionsCard from "../comparison-options-card";
import ActivityComparisonResultsTable from "./activity-comparison-results-table";
import ActivityComparisonEvents from "./activity-compariosn-events";
import ActivityComparisonStatementsChart
    from "./activity-comparison-statements-chart";
import ActivityComparisonWaitsChart from "./activity-comparison-waits-chart";
import StatementsTable from "./statements-table";
import Breadcrumb from "../../breadcrumb";

// Helpers
import ConditionalRender from "../../../helpers/ConditionalRender";
import { getComparisonInitialOptions, resetComparisonInitialOptions } from "../defaultValues";
import { formatPeriod, getTimeZone } from "../../../helpers/utils";

// Types
import CombinedChanges from "../../../types/instance/CombinedChanges";
import {
    ActivityComparisonOptions,
    ActivityComparisonProps, ActivityHashData,
} from "./types";

const ActivityComparison = (_props: ActivityComparisonProps) => {
    const [loading, setLoading] = useState<boolean>(false)
    const searchParams = new URLSearchParams(window.location.search)
    const {instances} = useContext(InstanceContext);
    const [generateId, setGenerateId] = useState<number>(0)
    const [showResultsTable, setShowResultsTable] = useState<boolean>(false)
    const [statementColors, setStatementColors] = useState<Object>({})
    const [waitsColors, setWaitsColors] = useState<Object>({})
    const [combinedChanges, setCombinedChanges] = useState<CombinedChanges[][]>([])
    const [waitTimes, setWaitTimes] = useState<number[]>([])
    const [statements, setStatements] = useState<ActivityHashData[][]>([]);
    const [formattedBreadCrumb, setFormattedBreadCrumb] = useState("");
    const navigate = useNavigate();
    const timezone = getTimeZone()

    const activityComparisonInitialOptions: ActivityComparisonOptions = getComparisonInitialOptions(instances, false)

    const [options, setOptions] = useState<ActivityComparisonOptions>(activityComparisonInitialOptions)

    useEffect(() => {
        if (searchParams.get('generate') === 'true') {
            setGenerateId(new Date().getTime());
            setShowResultsTable(true);
        }
    }, [options.firstInstanceOptions.instanceType, options.secondInstanceOptions.instanceType])

    const resetFilters = () => {
        const defaultValues = resetComparisonInitialOptions(false)
        setOptions(defaultValues);
    };

    const onOptionChange = (optionName: string, value: any) => {
        setOptions(prevOptions => {
            return {...prevOptions, [optionName]: value};
        });
    }

    const onGenerate = async () => {
        setLoading(true);
        if (!showResultsTable) {
            setShowResultsTable(true);
        }
        await new Promise(resolve => setTimeout(resolve, 0));
        setGenerateId(new Date().getTime());
    }

    const setChanges = (list: CombinedChanges[][]) => {
        setCombinedChanges(list)
        setLoading(false)
    }

    const updateUrlParams = (type: string, value: string) => {
        const query = window.location.search;
        let queryString = QueryString.parse(query);
        queryString[type] = value
        navigate(`?${QueryString.stringify(queryString)}`)
    }

    useEffect(() => {
        if (generateId) {
            updateUrlParams('chart', options.chart)
            updateUrlParams('topN', options.topN.toString())
            updateUrlParams('other', options.other.toString())
            updateUrlParams('showSqlId', options.showSqlId.toString())
            updateUrlParams('firstInstanceId', options.firstInstanceOptions.instanceId.toString())
            updateUrlParams('firstInstanceFrom', options.firstInstanceOptions.periodStart.toISOString())
            updateUrlParams('firstInstanceTo', options.firstInstanceOptions.periodEnd.toISOString())
            updateUrlParams('secondInstanceId', options.secondInstanceOptions.instanceId.toString())
            updateUrlParams('secondInstanceFrom', options.secondInstanceOptions.periodStart.toISOString())
            updateUrlParams('secondInstanceTo', options.secondInstanceOptions.periodEnd.toISOString())
            updateUrlParams('generate', 'false')
            if (options.firstInstanceOptions.snapshotId) {
                updateUrlParams('firstInstanceSnapshotId', options.firstInstanceOptions.snapshotId.toString())
            }
            if (options.secondInstanceOptions.snapshotId) {
                updateUrlParams('secondInstanceSnapshotId', options.secondInstanceOptions.snapshotId.toString())
            }
        }
    }, [generateId])

    const availableInstances = useMemo(() => {
        return instances.sort((a, b) => a.name.localeCompare(b.name));
    }, [instances]);

    useEffect(() => {
        if (!options?.firstInstanceOptions || !options?.secondInstanceOptions) {
            setFormattedBreadCrumb("");
            return;
        }

        const firstFormatted = formatPeriod(options.firstInstanceOptions, timezone);
        const secondFormatted = formatPeriod(options.secondInstanceOptions, timezone);

        setFormattedBreadCrumb(`${firstFormatted} / ${secondFormatted} (${timezone})`);
    }, [generateId]);

    return <React.Fragment>
        <PageContent title='Activity Comparison - DBmarlin' description={''}>
            {/* Heading */}
            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading="Activity Comparison">
                    <Link to="#">Reports</Link>
                    <NavLink to="/reports/activity-comparison" className={"current-breadcrumb-link"}>Activity Comparison</NavLink>
                    <span>{formattedBreadCrumb}</span>
                </Breadcrumb>
                <div className="col col-lg-5 col-md-6 col-sm-6 order-last icon text-md-end actions ms-auto">
                    <CopyUrl/>
                </div>
            </div>

            <div className="row row-cols-1">
                <div className="col">
                    <ComparisonOptionsCard loading={loading} options={options} type='activity'
                                           resetFilters={resetFilters}
                                           instances={availableInstances}
                                           onOptionsChange={onOptionChange} onGenerate={onGenerate}/>

                    <ConditionalRender if={showResultsTable}>
                        <ActivityComparisonResultsTable
                            instanceOptions={[options.firstInstanceOptions, options.secondInstanceOptions]}
                            setCombinedChanges={setChanges}
                            waitTimes={waitTimes} setWaitTimes={setWaitTimes}
                            options={options} generateId={generateId}/>
                    </ConditionalRender>

                    <ConditionalRender if={showResultsTable}>
                        <ActivityComparisonWaitsChart
                            instanceOptions={[options.firstInstanceOptions, options.secondInstanceOptions]}
                            setWaitsColors={setWaitsColors}
                            combinedChanges={combinedChanges}
                            options={options} generateId={generateId}/>
                        <ActivityComparisonStatementsChart
                            instanceOptions={[options.firstInstanceOptions, options.secondInstanceOptions]}
                            updateStatements={setStatements}
                            setStatementColors={setStatementColors}
                            combinedChanges={combinedChanges}
                            options={options} generateId={generateId}/>

                        <StatementsTable instanceOptions={[options.firstInstanceOptions, options.secondInstanceOptions]}
                                         waitTimes={waitTimes}
                                         statements={statements}
                                         waitsColors={waitsColors}
                                         combinedChanges={combinedChanges}
                                         options={options} generateId={generateId} statementColors={statementColors}/>
                    </ConditionalRender>

                    <ConditionalRender
                        if={showResultsTable && combinedChanges.length && (combinedChanges[0].length || combinedChanges[1].length || combinedChanges[2].length)}>
                        <ActivityComparisonEvents loading={loading} combinedChanges={combinedChanges}
                                                  generateId={generateId}
                                                  instanceOptions={[options.firstInstanceOptions, options.secondInstanceOptions]}/>
                    </ConditionalRender>
                </div>
            </div>

        </PageContent>

    </React.Fragment>
};

export default ActivityComparison;
